import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  AddAffiliationInfrastructureInputs,
  AffiliationActivityCounts,
  AffiliationBatchStudentsUsage,
  AffiliationCardDisplay,
  AffiliationCreativeFiles,
  AffiliationDisplay,
  AffiliationCreativeUploadUrlsInputs,
  AffiliationCreativeAzureUploadUrls,
  AffiliationInfrastructure,
  AffiliationProfileDisplay,
  AffiliationProfileInputs,
  AffiliationTeacherUsage,
  AffiliationUserCounts,
  AffiliationUserDisplay,
  AffiliationUserInfo,
  CourseSubjectAccess,
  ImplementationScoreRawData,
  NewAffiliationInputs,
  ProgramAffiliation,
  SchoolImplementationScore,
  StudentImplementationTrends,
  TeacherImplementationTrends,
  UpdateAffiliationInputs,
  UpdateAffiliationCreativesInputs,
} from "@/types/admin";
import { ApiResponse, IdNamePair } from "@/types/global";
import type {
  AffiliationMemberDisplay,
  GetAffiliationUsersParams,
  GetInternalUsersParams,
  InternalUserDisplay,
  UpdateAffiliationUserStatusInputs,
} from "@/types/userManagement";

export default class AdminApi extends ApiVerified {
  async getImplementationScoresByAffiliationType(
    affiliationTypeId: number,
    startDate: string | null,
    endDate: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolImplementationScore>>(
        `admin/get-implementation-scores-by-affiliation-type`,
        {
          params: { affiliationTypeId: affiliationTypeId, startDate: startDate, endDate: endDate },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getImplementationScoresByAem(
    userId: number,
    startDate: string | null,
    endDate: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolImplementationScore>>(
        `admin/get-implementation-scores-by-aem`,
        { params: { userId: userId, startDate: startDate, endDate: endDate } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCategoryWiseTeacherScore(
    affiliationId: number,
    startDate: string | null,
    endDate: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationTeacherUsage>(
        `admin/get-category-wise-teacher-scores`,
        { params: { affiliationId: affiliationId, startDate: startDate, endDate: endDate } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeacherImplementationTrends(
    affiliationId: number,
    startDate: string | null,
    endDate: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TeacherImplementationTrends>>(
        `admin/get-teacher-implementation-score-trends`,
        { params: { affiliationId: affiliationId, startDate: startDate, endDate: endDate } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCategoryWiseStudentScore(
    affiliationId: number,
    startDate: string | null,
    endDate: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationBatchStudentsUsage>(
        `admin/get-category-wise-student-scores`,
        {
          params: { affiliationId: affiliationId, startDate: startDate, endDate: endDate },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentImplementationTrends(
    affiliationId: number,
    startDate: string | null,
    endDate: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentImplementationTrends>>(
        `admin/get-student-implementation-score-trends`,
        {
          params: { affiliationId: affiliationId, startDate: startDate, endDate: endDate },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async downloadImplementationScoreData(
    affiliationTypeId: number,
    asOfDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ImplementationScoreRawData>(
        `admin/download-implementation-score-data`,
        { params: { affiliationTypeId: affiliationTypeId, asOfDate: asOfDate } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Absorbed from apiVerified.ts
  async getAffiliationsByName(affiliationName: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `admin/get-affiliations-by-name`,
        { params: { affiliationName: affiliationName } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getParentAffiliations(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `admin/get-parent-affiliations`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllAffiliations(userId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `admin/get-all-affiliations`,
        { params: { userId: userId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllDetailedAffiliations(
    onlyActive: boolean,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationCardDisplay>>(
        `admin/get-all-detailed-affiliations`,
        { params: { onlyActive: onlyActive, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedAffiliationsByType(
    affiliationTypeId: number,
    onlyActive: boolean,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationCardDisplay>>(
        `admin/get-detailed-affiliations-by-type`,
        {
          params: {
            affiliationTypeId: affiliationTypeId,
            onlyActive: onlyActive,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedAffiliationsByName(affiliationName: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationCardDisplay>>(
        `admin/get-detailed-affiliations-by-name`,
        { params: { affiliationName: affiliationName } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedAffiliationById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationDisplay>(
        `admin/get-detailed-affiliation-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationCreativesById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationCreativeFiles>(
        `admin/get-affiliation-creatives-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationInfrastructureById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationInfrastructure | null>(
        `admin/get-affiliation-infrastructure-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationProfileById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationProfileDisplay>(
        `admin/get-affiliation-profile-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUsersById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationUserInfo>>(
        `admin/get-affiliation-users-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationProgramsById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ProgramAffiliation>>(
        `admin/get-affiliation-programs-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationContentById(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<CourseSubjectAccess>>(
        `admin/get-affiliation-content-by-id`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Program-decoupled affiliation create — payload carries no AffiliationContent (programs are created
  // separately) and no AcademicSessionId (course-inherited). Returns the new affiliation id. aff-split-3-1 / R-13.
  async addNewAffiliation(affiliationData: NewAffiliationInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `admin/add-new-affiliation`,
        affiliationData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationCreativeFilesUrls(
    affiliationImageUploadUrlInputs: AffiliationCreativeUploadUrlsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AffiliationCreativeAzureUploadUrls>(
        `admin/get-affiliation-creative-upload-urls`,
        affiliationImageUploadUrlInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationCreatives(
    updateAffiliationCreativeFilesUrlsInputs: UpdateAffiliationCreativesInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `admin/update-affiliation-creatives`,
        updateAffiliationCreativeFilesUrlsInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Program-decoupled affiliation edit — UpdateAffiliationInputs carries no AffiliationContent, so editing
  // an affiliation never touches its programs. Returns the affiliation id. aff-split-3-1 / R-13.
  async updateAffiliation(affiliationData: UpdateAffiliationInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `admin/update-affiliation`,
        affiliationData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationProfile(affiliationData: AffiliationProfileInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `admin/update-affiliation-profile`,
        affiliationData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationStatus(affiliationId: number, isActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`admin/update-affiliation-status`, {
        affiliationId: affiliationId,
        isActive: isActive,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUserCounts(
    affiliationTypeId: number,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationUserCounts>>(
        `admin/get-affiliation-user-counts`,
        {
          params: { affiliationTypeId: affiliationTypeId, startDate: startDate, endDate: endDate },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationActivityCounts(
    affiliationTypeId: number,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationActivityCounts>>(
        `admin/get-affiliation-activity-counts`,
        {
          params: { affiliationTypeId: affiliationTypeId, startDate: startDate, endDate: endDate },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addAffiliationInfrastructure(
    addAffiliationInfrastructureInputs: AddAffiliationInfrastructureInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `admin/add-affiliation-infrastructure`,
        addAffiliationInfrastructureInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUsersByRole(roleId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `admin/get-affiliation-users-by-role`,
        { params: { roleId: roleId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUserByEmail(emailId: string, roleId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<IdNamePair>(
        `admin/get-affiliation-user-by-email`,
        { params: { emailId: emailId, roleId: roleId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUsersForMapping(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<{
        [affiliationUserRoleId: number]: Array<IdNamePair>;
      }>(`admin/get-affiliation-users-for-mapping`, { params: { onlyActive: onlyActive } });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUsersForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `admin/get-affiliation-users-for-affiliation`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Attendee picker for the AEM monthly-meeting form: the school's non-student members plus the
  // AEM who manages the school. getAffiliationUsersForAffiliation lists students too and misses the
  // AEM entirely (they hold no affiliation-user row), so the two are not interchangeable.
  async getAffiliationUsersForMonthlyMeeting(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `admin/get-affiliation-users-for-monthly-meeting`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // The SpeEdLabs Team tab. Platform-wide and admin-only by design: an internal user belongs to no
  // school, so there is no affiliationId to scope by — which is precisely why getAffiliationUsers
  // (an AffiliationUser join) returns nothing for these roles.
  async getInternalUsers(params: GetInternalUsersParams): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InternalUserDisplay>>(
        `admin/get-internal-users`,
        { params: params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedAffiliationUsers(userId: number | null): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationUserDisplay>>(
        `admin/get-detailed-affiliation-users`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // User Management v3 (pvni-user-mgmt-v3) — paginated affiliation member list (SP1, api-binding §8).
  // GET /v7/admin/get-affiliation-users → PaginatedResponse<AffiliationMemberDisplay>; count/pages are
  // surfaced on ApiResponse via isPaginated=true. batchId is required iff roleIdsCsv includes a student
  // role (BR-UI-601 — manageUserStore pre-checks + synthesises the 422); an unset batchId is dropped by
  // the apiVerified paramsSerializer. Bare name (no suffix) stays distinct from the
  // getAffiliationUsersBy*/For* family (architect m1) and matches the api-binding §8 contract + the
  // usermgmt-2-5 manageUserStore consumer.
  async getAffiliationUsers(params: GetAffiliationUsersParams): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationMemberDisplay>>(
        `admin/get-affiliation-users`,
        { params: params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // User Management v3 (pvni-user-mgmt-v3) — toggle member active status (SP1, api-binding §9).
  // POST /v7/admin/update-affiliation-user-status → true. 417 = same-value (not idempotent) — the
  // manageUserStore disables the in-flight toggle (BR-UI-705). 404 "Affiliation user not found".
  async updateAffiliationUserStatus(
    inputs: UpdateAffiliationUserStatusInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `admin/update-affiliation-user-status`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
